'use client';

import {
  useRef,
  useEffect,
  useState,
  useCallback,
  KeyboardEvent,
} from 'react';
import type { BugReport } from '../context/types';
import { useDebugChat } from '../hooks/useDebugChat';
import { DebugChatMessage } from './DebugChatMessage';
import { BugReportView } from './BugReportView';
import { AgentPromptView } from './AgentPromptView';

interface Props {
  onMinimize: () => void;
}

type ActiveView = 'chat' | 'bugReport' | 'agentPrompt';

export function DebugChatPanel({ onMinimize }: Props) {
  const { messages, isLoading, sendMessage, generateBugReport, generateAgentPrompt } =
    useDebugChat();

  const [inputValue, setInputValue] = useState('');
  const [activeView, setActiveView] = useState<ActiveView>('chat');
  const [activeBugReport, setActiveBugReport] = useState<BugReport | null>(null);
  const [activeAgentPrompt, setActiveAgentPrompt] = useState<string | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  const handleSend = useCallback(() => {
    if (!inputValue.trim() || isLoading) return;
    sendMessage(inputValue.trim());
    setInputValue('');
    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  }, [inputValue, isLoading, sendMessage]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend],
  );

  const handleTextareaChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setInputValue(e.target.value);
      // Auto-grow textarea
      const ta = e.target;
      ta.style.height = 'auto';
      ta.style.height = `${Math.min(ta.scrollHeight, 100)}px`;
    },
    [],
  );

  const handleViewBugReport = useCallback((report: BugReport) => {
    setActiveBugReport(report);
    setActiveView('bugReport');
  }, []);

  const handleViewAgentPrompt = useCallback((prompt: string) => {
    setActiveAgentPrompt(prompt);
    setActiveView('agentPrompt');
  }, []);

  const handleBackToChat = useCallback(() => {
    setActiveView('chat');
  }, []);

  return (
    <div className="bg-surface-1 border-stroke flex h-full flex-col overflow-hidden rounded-xl border shadow-2xl">
      {/* Header */}
      <div className="bg-surface-2 border-stroke flex items-center justify-between border-b px-3 py-2.5">
        <div className="flex items-center gap-2">
          {activeView !== 'chat' && (
            <button
              type="button"
              onClick={handleBackToChat}
              className="text-text-secondary hover:text-text-primary mr-1 text-sm transition-colors"
              aria-label="Back to chat"
            >
              ←
            </button>
          )}
          <span className="text-text-primary text-sm font-semibold">
            {activeView === 'chat'
              ? 'Debug Assistant'
              : activeView === 'bugReport'
                ? 'Bug Report'
                : 'Agent Prompt'}
          </span>
          {isLoading && (
            <span className="bg-accent h-1.5 w-1.5 animate-pulse rounded-full" />
          )}
        </div>
        <button
          type="button"
          onClick={onMinimize}
          className="text-text-secondary hover:text-text-primary flex h-6 w-6 items-center justify-center rounded transition-colors hover:bg-white/10"
          aria-label="Minimize debug chat"
        >
          ✕
        </button>
      </div>

      {/* Content area */}
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        {activeView === 'chat' ? (
          <>
            {/* Messages */}
            <div className="flex-1 space-y-3 overflow-y-auto p-3">
              {messages.length === 0 && (
                <p className="text-text-tertiary pt-4 text-center text-xs">
                  Ask about errors, generate a bug report, or get an agent
                  prompt to fix issues.
                </p>
              )}
              {messages.map((msg) => (
                <DebugChatMessage
                  key={msg.id}
                  message={msg}
                  onViewBugReport={handleViewBugReport}
                  onViewAgentPrompt={handleViewAgentPrompt}
                />
              ))}
              {isLoading && (
                <div className="flex items-start gap-2">
                  <div className="bg-surface-2 rounded-xl px-3 py-2">
                    <span className="text-text-tertiary text-xs">
                      Thinking
                      <span className="animate-pulse">...</span>
                    </span>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Action bar */}
            <div className="border-stroke border-t px-3 py-2">
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={generateBugReport}
                  disabled={isLoading}
                  className="bg-surface-2 text-text-secondary hover:text-text-primary rounded-lg px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-50"
                >
                  Bug Report
                </button>
                <button
                  type="button"
                  onClick={generateAgentPrompt}
                  disabled={isLoading}
                  className="bg-surface-2 text-text-secondary hover:text-text-primary rounded-lg px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-50"
                >
                  Agent Prompt
                </button>
              </div>
            </div>

            {/* Input area */}
            <div className="bg-surface-2 border-stroke flex items-end gap-2 border-t p-3">
              <textarea
                ref={textareaRef}
                value={inputValue}
                onChange={handleTextareaChange}
                onKeyDown={handleKeyDown}
                placeholder="Describe the issue... (Enter to send)"
                rows={1}
                className="bg-surface-1 text-text-primary placeholder:text-text-tertiary border-stroke flex-1 resize-none rounded-lg border px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-white/20"
                style={{ minHeight: '36px', maxHeight: '100px' }}
              />
              <button
                type="button"
                onClick={handleSend}
                disabled={!inputValue.trim() || isLoading}
                className="bg-accent hover:bg-accent/80 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-white transition-colors disabled:opacity-40"
                aria-label="Send message"
              >
                ↑
              </button>
            </div>
          </>
        ) : activeView === 'bugReport' && activeBugReport ? (
          <div className="flex-1 overflow-y-auto p-3">
            <BugReportView report={activeBugReport} />
          </div>
        ) : activeView === 'agentPrompt' && activeAgentPrompt ? (
          <div className="flex-1 overflow-y-auto p-3">
            <AgentPromptView prompt={activeAgentPrompt} />
          </div>
        ) : null}
      </div>
    </div>
  );
}
